import { useMemo } from "react";
import { Link } from "react-router";

import { useGetCollectionsQuery } from "@/api/api";
import type { HGCollection, HGUser } from "@/common/types";
import { collectionPath, userPath, userRef } from "@/common/urls";
import UserAvatar from "../UserAvatar";

/** How many collections a card can name before it stops being a card. */
const MAX_CHIPS = 4;

/**
 * One featured space, with a few of its public collections as the way in.
 *
 * The collections are the point. `/builtin-users` carries a name, a handle and a
 * picture and nothing else — no bio, no counts — so a card built from it alone
 * says only that a person exists. `GET /collections?user_id=` needs no session
 * and returns public rows only to anyone but the owner (see the endpoint), which
 * is exactly the shelf this page wants to show.
 *
 * The query lives in this component, one instance per user, because a hook
 * cannot be called from inside the parent's `.map`.
 */
export default function FeaturedUserCard({ user }: { user: HGUser }) {
  const { data } = useGetCollectionsQuery(userRef(user));

  const chips = useMemo(() => {
    if (!data) return [];
    return (
      data
        // The default collection is not a collection anyone named: it is where
        // unfiled records go, and `getCollections` relabels it "Without
        // collection" for the sidebar. Nothing to advertise.
        .filter((collection) => !collection.is_default)
        // The owner's own pins first — they are the closest thing to a statement
        // about which of these matters — then the title order the query already
        // sorted into.
        .slice()
        .sort(byPinThenOrder)
        .slice(0, MAX_CHIPS)
    );
  }, [data]);

  const name = user.name || user.nickname || `User #${user.id}`;

  return (
    <div className="group rounded-xl border bg-card p-4 transition-colors duration-150 hover:bg-row-hover hover:shadow-[inset_3px_0_0_var(--primary)]">
      <Link
        to={userPath(user)}
        className="flex min-w-0 items-center gap-3 outline-none"
      >
        <UserAvatar
          name={user.name || user.nickname}
          picture={user.picture}
          className="size-11 shrink-0"
          fallbackClassName="text-[13px] font-medium"
        />
        <div className="min-w-0">
          <div
            className="truncate text-[15.5px] font-semibold text-foreground group-hover:text-accent-foreground"
            style={{ fontFamily: "var(--font-serif)" }}
          >
            {name}
          </div>
          {user.nickname && (
            <div className="truncate font-mono text-[11px] text-muted-foreground">
              @{user.nickname}
            </div>
          )}
        </div>
      </Link>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5 border-t pt-3">
          {chips.map((collection) => (
            <Link
              key={collection.id}
              to={collectionPath(collection)}
              className="max-w-full truncate rounded-[4px] border bg-background px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:border-primary hover:text-accent-foreground"
            >
              {collection.title}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** Pinned collections first, in pin order; everything else keeps its place. */
function byPinThenOrder(a: HGCollection, b: HGCollection) {
  const pinA = a.pin_position ?? Number.MAX_SAFE_INTEGER;
  const pinB = b.pin_position ?? Number.MAX_SAFE_INTEGER;
  return pinA - pinB;
}
